import React, { useEffect, useState } from "react";
import { Pencil, Save, X, Plus, Trash2, RefreshCcw } from "lucide-react";
import { adminProducts } from "../../services/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(n || 0);

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminProducts.list();
      setProducts(data.products);
    } catch {
      toast.error("Error al cargar productos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (product) => {
    if (!window.confirm(`¿Eliminar "${product.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await adminProducts.remove(product.id);
      toast.success("Producto eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleSync = async () => {
    if (
      !window.confirm(
        "Sincronizar catálogo: restaurará el Acondicionador, actualizará las fotos nuevas, renombrará el Jabón exfoliante y la crema de cebo de res, y agregará Aceite de ducha, Aceite seco y Refill si no existen. ¿Continuar?"
      )
    )
      return;
    setSyncing(true);
    try {
      const result = await adminProducts.syncCatalog();
      const added = result.details.productos_agregados || [];
      toast.success("Catálogo sincronizado", {
        description: added.length
          ? `Agregados: ${added.join(", ")}`
          : "Fotos y productos actualizados",
      });
      load();
    } catch (err) {
      toast.error("Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-products-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Productos</h1>
          <p className="text-stone-500 mt-1">
            {products.length} producto{products.length === 1 ? "" : "s"} en el catálogo
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleSync}
            disabled={syncing}
            variant="outline"
            className="border-green-200 text-green-800 hover:bg-green-50"
            data-testid="admin-sync-catalog"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizando..." : "Sincronizar catálogo"}
          </Button>
          <Button
            onClick={() => setCreating(true)}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="admin-new-product"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo producto
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="text-center text-stone-400 py-12">Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div
              key={p.id}
              className="bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`product-card-${p.id}`}
            >
              <div className="aspect-square bg-stone-100 overflow-hidden">
                {p.image && (
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-stone-800 mb-1 line-clamp-1">
                  {p.name}
                </h3>
                <p className="text-xs text-stone-500 mb-3 line-clamp-2">
                  {p.description}
                </p>
                <div className="space-y-1 mb-3">
                  {(p.variants || []).map((v, i) => (
                    <div
                      key={i}
                      className="flex justify-between text-xs text-stone-600"
                    >
                      <span>{v.size}</span>
                      <span className="font-semibold">{fmt(v.price)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setEditing(p)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-amber-200 text-amber-800 hover:bg-amber-50"
                    data-testid={`edit-product-${p.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-2" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleDelete(p)}
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-600 hover:bg-red-50 px-3"
                    data-testid={`delete-product-${p.id}`}
                    aria-label="Eliminar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProductEditModal
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {creating && (
        <ProductEditModal
          product={{
            id: null,
            name: "",
            category: "",
            description: "",
            image: "",
            featured: false,
            variants: [{ size: "", price: 0 }],
          }}
          isNew={true}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function ProductEditModal({ product, onClose, onSaved, isNew = false }) {
  const [form, setForm] = useState({
    name: product.name || "",
    category: product.category || "",
    description: product.description || "",
    image: product.image || "",
    featured: product.featured || false,
    variants: product.variants ? [...product.variants] : [{ size: "", price: 0 }],
  });
  const [saving, setSaving] = useState(false);

  const updateVariant = (idx, field, value) => {
    const variants = [...form.variants];
    variants[idx] = { ...variants[idx], [field]: value };
    setForm({ ...form, variants });
  };

  const addVariant = () => {
    setForm({
      ...form,
      variants: [...form.variants, { size: "", price: 0 }],
    });
  };

  const removeVariant = (idx) => {
    setForm({
      ...form,
      variants: form.variants.filter((_, i) => i !== idx),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const variants = form.variants
        .filter((v) => v.size?.trim())
        .map((v) => ({ size: v.size, price: parseFloat(v.price) || 0 }));

      const payload = {
        name: form.name,
        category: form.category,
        description: form.description,
        image: form.image,
        featured: form.featured,
        variants,
      };

      if (isNew) {
        payload.ingredients = [];
        await adminProducts.create(payload);
        toast.success("Producto creado");
      } else {
        await adminProducts.update(product.id, payload);
        toast.success("Producto actualizado");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[95vh] sm:rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-edit-modal"
      >
        <div className="px-6 py-4 border-b border-stone-200 flex justify-between items-center">
          <h2 className="text-lg font-bold text-stone-800">
            {isNew ? "Nuevo producto" : `Editar producto #${product.id}`}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <Label>Nombre *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
              data-testid="edit-product-name"
              placeholder="Ej: Crema hidratante"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Categoría *</Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1"
                placeholder="Ej: cabello, corporal, facial"
              />
            </div>
            <div>
              <Label>Imagen (ruta o URL)</Label>
              <Input
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="mt-1"
                placeholder="/images/mi-producto.png"
              />
            </div>
          </div>

          <div>
            <Label>Descripción *</Label>
            <Textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={5}
              className="mt-1"
              data-testid="edit-product-description"
              placeholder="Descripción del producto, ingredientes principales, beneficios..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Variantes / Precios</Label>
              <button
                onClick={addVariant}
                className="text-sm text-amber-700 hover:text-amber-800 flex items-center gap-1"
                data-testid="add-variant"
              >
                <Plus className="w-3.5 h-3.5" />
                Agregar
              </button>
            </div>
            <div className="space-y-2">
              {form.variants.map((v, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    placeholder="Tamaño / variante"
                    value={v.size}
                    onChange={(e) => updateVariant(i, "size", e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Precio"
                    value={v.price}
                    onChange={(e) => updateVariant(i, "price", e.target.value)}
                    className="w-32"
                  />
                  <button
                    onClick={() => removeVariant(i)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-2 bg-stone-50">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-700 hover:bg-amber-800"
            data-testid="save-product-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
